import { SIMA as SIMADoc } from "@earthbrain/sima";
import { createGuid } from "cesium";
import { CesiumRenderableSIMA, } from "./cesium-renderable-SIMA";
import { exportToSIMAFile } from "./sima-exporter";
import { SIMA } from "./sima";
export class SIMAManager {
    _viewer;
    _primitives;
    _simaMap = new Map();
    constructor(options) {
        if (options) {
            if ("scene" in options) {
                this._viewer = options;
            }
            else {
                this._viewer = options.viewer;
                this._primitives = options.primitives;
            }
        }
    }
    get viewer() {
        return this._viewer;
    }
    set viewer(viewer) {
        this._viewer = viewer;
    }
    get simaMap() {
        return this._simaMap;
    }
    async loadSIMA(arg1, arg2, arg3) {
        let id;
        let content;
        let options;
        let customIdProvided = false;
        if (typeof arg1 === "string" &&
            (typeof arg2 === "string" || arg2 instanceof ArrayBuffer)) {
            customIdProvided = true;
            id = arg1;
            content = arg2;
            options = arg3;
        }
        else {
            id = createGuid();
            content = arg1;
            options = arg2;
        }
        let textContent;
        if (typeof content === "string") {
            textContent = content;
        }
        else {
            try {
                const decoder = new TextDecoder("shift-jis");
                textContent = decoder.decode(content);
            }
            catch (_e) {
                const decoder = new TextDecoder("utf-8");
                textContent = decoder.decode(content);
            }
        }
        const simaDoc = SIMADoc.parse(textContent);
        const renderable = await this.addSIMA(id, simaDoc, options);
        return customIdProvided ? renderable : id;
    }
    /**
     * Adds a SIMA document instance to the manager, renders it, and adds primitives to the scene.
     */
    async addSIMA(id, simaDoc, options) {
        if (this._simaMap.has(id)) {
            this.removeSIMA(id);
        }
        const sima = SIMA.fromSIMADoc(simaDoc, id);
        const renderable = await CesiumRenderableSIMA.create(sima, options, this._viewer);
        const primitiveCol = renderable.render();
        const parentCollection = this._primitives || this._viewer?.scene?.primitives;
        if (parentCollection && !parentCollection.isDestroyed()) {
            parentCollection.add(primitiveCol);
        }
        this._simaMap.set(id, renderable);
        return renderable;
    }
    /**
     * Retrieves the CesiumRenderableSIMA instance for the given ID.
     */
    getSIMA(id) {
        return this._simaMap.get(id);
    }
    /**
     * Retrieves the underlying SIMA document for the given ID.
     */
    getSIMADoc(id) {
        return this._simaMap.get(id)?.simaDoc;
    }
    /**
     * Checks if a SIMA document with the given ID has a central line.
     */
    hasCentralLine(id) {
        const sima = this.getSIMA(id);
        return sima ? sima.hasCentralLine() : false;
    }
    /**
     * Checks if a SIMA document with the given ID exists.
     */
    hasSIMA(id) {
        return this._simaMap.has(id);
    }
    /**
     * Removes and disposes the SIMA renderable associated with the given ID.
     */
    removeSIMA(id) {
        const renderable = this._simaMap.get(id);
        if (!renderable) {
            return false;
        }
        const parentCollection = this._primitives || this._viewer?.scene?.primitives;
        if (parentCollection &&
            !parentCollection.isDestroyed() &&
            renderable.primitives) {
            parentCollection.remove(renderable.primitives);
        }
        renderable.destroy();
        this._simaMap.delete(id);
        return true;
    }
    /**
     * Shows the rendered primitives for the given SIMA ID.
     */
    show(id) {
        this.setVisibility(id, true);
    }
    /**
     * Hides the rendered primitives for the given SIMA ID.
     */
    hide(id) {
        this.setVisibility(id, false);
    }
    /**
     * Sets visibility for a specific SIMA ID.
     */
    setVisibility(id, visible) {
        const renderable = this._simaMap.get(id);
        if (renderable) {
            renderable.show = visible;
        }
    }
    /**
     * Gets visibility of a specific SIMA ID.
     */
    isVisible(id) {
        const renderable = this._simaMap.get(id);
        return renderable ? renderable.show : false;
    }
    /**
     * Shows all SIMA renderables in the map.
     */
    showAll() {
        this.setAllVisibility(true);
    }
    /**
     * Hides all SIMA renderables in the map.
     */
    hideAll() {
        this.setAllVisibility(false);
    }
    /**
     * Sets visibility for all SIMA renderables.
     */
    setAllVisibility(visible) {
        for (const renderable of this._simaMap.values()) {
            renderable.show = visible;
        }
    }
    /**
     * Exports the SIMA document matching the ID to SIMA text format.
     */
    exportSIMA(id, options) {
        const simaDoc = this.getSIMADoc(id);
        if (!simaDoc) {
            throw new Error(`SIMA document with ID '${id}' not found.`);
        }
        return simaDoc.export(options);
    }
    /**
     * Exports the SIMA document matching the ID to a .sim file download.
     */
    exportSIMAToFile(id, fileName, siteName) {
        const simaDoc = this.getSIMADoc(id);
        if (!simaDoc) {
            throw new Error(`SIMA document with ID '${id}' not found.`);
        }
        const site = siteName ?? simaDoc.fileAttributeData?.site_name ?? "";
        const targetFileName = fileName ?? `${id}.sim`;
        exportToSIMAFile(simaDoc.toFormat(), site, targetFileName);
    }
    /**
     * Returns array of all stored SIMA document IDs.
     */
    getIds() {
        return Array.from(this._simaMap.keys());
    }
    /**
     * Clears all SIMA documents and primitives.
     */
    clear() {
        const ids = Array.from(this._simaMap.keys());
        for (const id of ids) {
            this.removeSIMA(id);
        }
    }
    /**
     * Alias for clear / cleanup.
     */
    destroy() {
        this.clear();
    }
    /**
     * Alias for destroy.
     */
    dispose() {
        this.destroy();
    }
}
//# sourceMappingURL=sima-manager.js.map